/* import __COLOCATED_TEMPLATE__ from './lead-details-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action, get as emberGet } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type { MemoryFieldColumn } from 'embercom/lib/ai-automation/sdr-memory-fields';
import { toDisplayLabel, toUserAttributeKey } from 'embercom/lib/ai-automation/sdr-memory-fields';
import type User from 'embercom/models/user';

type UserInstance = InstanceType<typeof User>;

const CORE_COMPANY_IDENTIFIERS = [
  'company.name',
  'company.industry',
  'company.size',
  'company.website',
  'company.monthly_spend',
] as const;

const CORE_COMPANY_FIELDS: MemoryFieldColumn[] = CORE_COMPANY_IDENTIFIERS.map((identifier) => ({
  identifier,
  key: toUserAttributeKey(identifier),
  label: toDisplayLabel(identifier),
  isCompany: true,
}));

type ResolvedField = {
  label: string;
  value: string;
};

interface Signature {
  Args: {
    user: UserInstance;
    dynamicColumns: MemoryFieldColumn[];
    onClose: () => void;
  };
  Blocks: {};
  Element: null;
}

export default class LeadDetailsDrawer extends Component<Signature> {
  @service declare intercomEventService: $TSFixMe;

  @tracked leadSectionOpen = true;
  @tracked companySectionOpen = true;

  private resolveFields(columns: MemoryFieldColumn[]): ResolvedField[] {
    let user = this.args.user;
    return columns.map((col) => {
      let raw = emberGet(user, col.key as keyof UserInstance);
      let value = raw != null && raw !== '' ? String(raw) : '-';
      return { label: col.label, value };
    });
  }

  get leadFields(): ResolvedField[] {
    return this.resolveFields(this.args.dynamicColumns.filter((c) => !c.isCompany));
  }

  get companyFields(): ResolvedField[] {
    let memoryFields = this.args.dynamicColumns.filter((c) => c.isCompany);
    let memoryIdentifiers = new Set(memoryFields.map((f) => f.identifier));
    let extraFields = CORE_COMPANY_FIELDS.filter((f) => !memoryIdentifiers.has(f.identifier));
    return this.resolveFields([...memoryFields, ...extraFields]).map((field) => ({
      ...field,
      label: field.label.replace(/^Company\s+/, '').replace(/^\w/, (c) => c.toUpperCase()),
    }));
  }

  @action
  toggleLeadSection() {
    this.leadSectionOpen = !this.leadSectionOpen;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'toggled',
      object: 'lead_section',
      place: 'fin_sdr_qualification_analyze',
      section: 'leads_drawer',
      expanded: this.leadSectionOpen,
    });
  }

  @action
  toggleCompanySection() {
    this.companySectionOpen = !this.companySectionOpen;
    this.intercomEventService.trackAnalyticsEvent({
      action: 'toggled',
      object: 'company_section',
      place: 'fin_sdr_qualification_analyze',
      section: 'leads_drawer',
      expanded: this.companySectionOpen,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAutomation::Analyze::Leads::LeadDetailsDrawer': typeof LeadDetailsDrawer;
    'ai-automation/analyze/leads/lead-details-drawer': typeof LeadDetailsDrawer;
  }
}
